// @flow
import React, { PureComponent, Fragment } from 'react';
import type { MemberType, DepartmentType, ArbitreType } from 'types/PageDetail';
import type { FullUserType } from 'types/User';
import Highlighter from "react-highlight-words";
import DropdownRadioInput from 'components/fragments/input/DropdownRadioInput';
import { dynamicClassName } from 'utils/dynamicClassName';
import { handleTagEvent }from 'utils/tagManagerUtils';

export type StateProps = {
  userPref: FullUserType
};

type Props = {
  userPref: FullUserType,
  membersGroups?: Array<{
    name: string,
    child: Array<MemberType>
  }>,
  title: string,
  departmentsGroups?: Array<DepartmentType>,
  arbitresGroups?: Array<ArbitreType>
};

type ComponentState = {
  search: string,
  selectedlist: string,
  isOpen: boolean,
  indexOpen: number
}

class MemberPageDetail extends PureComponent <Props, ComponentState> {
  state: ComponentState = {
    search: '',
    selectedlist: '',
    isOpen: false,
    indexOpen: 0
  };

  renderRow = (membersGroup: Array<MemberType>): any => {
    const { userPref } = this.props;
    const searchWords = this.state.search.split(' ');

    return membersGroup.map<any>((member: MemberType, index): any => {
      return(
        <li key={index} className="member member--row">
          <div>
            <p className="member__title ft-h4">
              <Highlighter
                searchWords={searchWords}
                autoEscape={true}
                textToHighlight={member.title}
              />
            </p>
            {member.roles &&
              <p className="member__role ft-h6 ft-up">
                {member.roles.map<any>((role, index) => {
                  return(
                    <Fragment key={index}>
                      <Highlighter
                        searchWords={searchWords}
                        autoEscape={true}
                        textToHighlight={role}
                      />
                      <br/>
                    </Fragment>
                  );
                })}
              </p>
            }
          </div>
          <p className="member__info">
            {member.email &&
              <a
                href={`mailto:${member.email}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleTagEvent(
                  'module_contact',
                  'clic mail',
                  window.location.pathname,
                  userPref                  
                )}>
                <i className="icon icon-mail is-inline"></i>
                  <Highlighter
                    searchWords={searchWords}
                    autoEscape={true}
                    textToHighlight={member.email}
                  />
              </a>
            }
            {member.telephone &&
              <a href={`tel:${member.telephone}`} target="_blank" rel="noopener noreferrer">
                <i className="icon icon-phone is-inline"></i>
                <span>
                  <Highlighter
                    searchWords={searchWords}
                    autoEscape={true}
                    textToHighlight={member.telephone}
                  />
                </span>
              </a>
            }
            {member.website &&
              <a
                href={`${member.website}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleTagEvent(
                  'module annuaire',
                  'clic website',
                  window.location.pathname,
                  userPref
                )}>
                <i className="icon icon-external is-inline"></i>
                <span>Site Internet</span>
              </a>
            }
            {member.date_naissance &&
              <span>
                <i className="icon icon-account is-inline"></i>
                {member.date_naissance}<br />
              </span>
            }
            {member.structure_arbitrage &&
              <span>
                <i className="icon icon-place is-inline"></i>
                {member.structure_arbitrage}<br />
              </span>
            }
            {member.adresse &&
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${member.adresse}+${member.code_postal}+${member.ville}`}
                title="Voir l'adresse sur Google Map  (nouvel onglet)"
                target="_blank"
                rel="noopener noreferrer">
                <i className="icon icon-place is-inline"></i>
                <span>
                  <Highlighter
                    searchWords={searchWords}
                    autoEscape={true}
                    textToHighlight={member.adresse}
                  /><br />
                  {member.code_postal && 
                    <Highlighter
                      searchWords={searchWords}
                      autoEscape={true}
                      textToHighlight={member.code_postal} /> }
                  <span> </span>
                  {member.ville && 
                    <Highlighter
                      searchWords={searchWords}
                      autoEscape={true}
                      textToHighlight={member.ville}
                    />}
                </span>
              </a>}
          </p>
        </li>
      );
    });
  }

  renderGroup = () => {
    const { membersGroups } = this.props;
    let membersGroupsFiltered = membersGroups;

    if (!membersGroups || !membersGroupsFiltered) {
      return null;
    }

    if (this.state.search !== '') {
      membersGroupsFiltered = membersGroups.map<any>(membersGroup => {
        return {
          ...membersGroup,
          child: membersGroup.child.filter(members =>
            (members.title && members.title.toLowerCase().includes(this.state.search.toLowerCase()))
            || (members.role && members.role.toLowerCase().includes(this.state.search.toLowerCase()))
            || (members.email && members.email.toLowerCase().includes(this.state.search.toLowerCase()))
            || (members.telephone && members.telephone.toLowerCase().includes(this.state.search.toLowerCase()))
            || (members.adresse && members.adresse.toLowerCase().includes(this.state.search.toLowerCase()))
            || (members.ville && members.ville.toLowerCase().includes(this.state.search.toLowerCase()))
            || (members.code_postal && members.code_postal.toLowerCase().includes(this.state.search.toLowerCase()))
          )
        };
      });
    }

    return membersGroupsFiltered.map<any>((membersGroup: {name: string,child: Array<MemberType>}, index) => {
      if (membersGroup.child.length > 0) {
        return (
          <div key={index} className="row directory">
            <div className="col col-lg-2on9">
              <span className="directory__ref ft-700">{membersGroup.name}</span>
            </div>
            <div className="col col-lg-7on9">
              <div className="directory__items">
                <ul className="directory__members box box--smoke">
                  {this.renderRow(membersGroup.child)}
                </ul>
              </div>
            </div>
          </div>
        );
      }
      return false;
    });
  }

  renderDepartements = () => {
    const { userPref, departmentsGroups } = this.props;
    let departmentsGroupsFiltered = departmentsGroups;

    if (!departmentsGroups || !departmentsGroupsFiltered) {
      return null;
    }

    if (this.state.selectedlist !== '') {
      departmentsGroupsFiltered = departmentsGroups.filter(department => department.title === this.state.selectedlist);
    }

    return departmentsGroupsFiltered.map<any>((departmentsGroup: DepartmentType, index) => {
      if (departmentsGroup.personnes.length > 0) {
        const { indexOpen, isOpen } = this.state;

        const childClassName = dynamicClassName("directory__members box box--smoke accordion-panel");
        const triggerClassName = dynamicClassName("btn btn--icon accordion-trigger");
        if (!isOpen || indexOpen !== index) {
          childClassName.add("is-hidden");
        }
        if (isOpen && indexOpen === index) {
          triggerClassName.add("is-selected");
        }

        return (
          <Fragment key={index}>
            <div className="col col-lg-2on9">
              <span className="directory__ref ft-700">{departmentsGroup.letter}</span>
            </div>
            <div className="col col-lg-7on9">
              <div className="directory__items">
                <div className="member member--area box">
                  <div>
                    <p className="member__title ft-h3">{departmentsGroup.title}</p>
                  </div>
                  <p className="member__info">
                    {departmentsGroup.mail &&
                      <a
                        href={`mailto:${departmentsGroup.mail}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={handleTagEvent(
                          'module departement',
                          'clic mail',
                          window.location.pathname,
                          userPref
                        )}>
                        <i className="icon icon-mail is-inline"></i>
                        <span>{departmentsGroup.mail}</span>
                      </a>
                    }
                    {departmentsGroup.phone &&
                      <a href={`tel:${departmentsGroup.phone}`} target="_blank" rel="noopener noreferrer">
                        <i className="icon icon-phone is-inline"></i>
                        <span>{departmentsGroup.phone}</span>
                      </a>
                    }
                    {departmentsGroup.adress &&
                      <a
                        href={`https://www.google.com/maps/dir/?api=1&destination=${departmentsGroup.adress.street}+${departmentsGroup.adress.postalCode}+${departmentsGroup.adress.locality}`}
                        title="Voir l'adresse sur Google Map  (nouvel onglet)"
                        target="_blank"
                        rel="noopener noreferrer">
                        <i className="icon icon-place is-inline"></i>
                        <span>
                          {departmentsGroup.adress.street}<br/>
                          {departmentsGroup.adress.postalCode}<br/>
                          {departmentsGroup.adress.locality}
                        </span>
                      </a>
                    }
                  </p>
                  <a 
                    role="button"
                    tabIndex={0}
                    className={triggerClassName.build()} 
                    title="Afficher les membres" 
                    onClick={this.handleClickDepartment(index)}>
                    <i className="icon icon-zoom_plus "></i>
                  </a>
                </div>
                <ul className={childClassName.build()}>
                  {this.renderRow(departmentsGroup.personnes)}
                </ul>
              </div>
            </div>
          </Fragment>
        );
      }
      return false;
    });
  }

  renderArbitres = () => {
    const { arbitresGroups } = this.props;
    let arbitresGroupsFiltered = arbitresGroups;

    if (!arbitresGroups || !arbitresGroupsFiltered) {
      return null;
    }

    if (this.state.selectedlist !== '') {
      arbitresGroupsFiltered = arbitresGroupsFiltered.filter(arbitre => arbitre.title === this.state.selectedlist);
    }

    return arbitresGroupsFiltered.map<any>((arbitresGroup: ArbitreType, index) => {
      if (arbitresGroup.personnes.length > 0) {
        const { indexOpen, isOpen } = this.state;

        const childClassName = dynamicClassName("directory__members box box--smoke accordion-panel");
        const triggerClassName = dynamicClassName("btn btn--icon accordion-trigger");
        if (!isOpen || indexOpen !== index) {
          childClassName.add("is-hidden");
        }
        if (isOpen && indexOpen === index) {
          triggerClassName.add("is-selected");
        }

        return (
          <Fragment key={index}>
            <div className="col">
              <div className="directory__items">
                <div className="member member--area box">
                  <div>
                    <p className="member__title ft-h3">{arbitresGroup.title}</p>
                  </div>
                  <a 
                    className={triggerClassName.build()}
                    role="button"
                    tabIndex={0}
                    title="Afficher les membres" 
                    onClick={this.handleClickDepartment(index)}>
                    <i className="icon icon-zoom_plus "></i>
                  </a>
                </div>
                <ul className={childClassName.build()}>
                  {this.renderRow(arbitresGroup.personnes)}
                </ul>
              </div>
            </div>
          </Fragment>
        );
      }
      return false;
    });
  }

  handleClickDepartment = (index: number) => {
    return () => {
      if (this.state.isOpen && index !== this.state.indexOpen) {
        this.setState({
          indexOpen: index
        });
      } else {
        this.setState(prevState => ({
          isOpen: !prevState.isOpen,
          indexOpen: index
        }));
      }
    };
  }

  handleChange = (e: Event) => {
    if (e.target instanceof HTMLInputElement) {
      this.setState({
        search: e.target.value
      });
    }
  }

  handleDropdownChange = (value: string) => {
    if (value !== '') {
      this.setState({
        selectedlist: value,
        isOpen: true
      });
    } else {
      this.setState({
        selectedlist: value,
        isOpen: false
      });
    }
  }

  render() {
    const { membersGroups, departmentsGroups, arbitresGroups, title } = this.props;
    const { isOpen } = this.state;
    const rowClassName = dynamicClassName("row directory");
    isOpen && rowClassName.add("has-accordion-open");

    if (membersGroups) {
      const placeholder = title.match('igues') ? "Saisir le nom ou la région" : "Saisir le nom ou un département";
      return (
        <Fragment>
          <div className="page__head mb-3">
            <h3>{title}</h3>
            <div className="input input--search " data-overlay="false">
              <input 
                type="search" 
                id="ioudf892" 
                placeholder={placeholder}
                onChange={this.handleChange}/>
              <label htmlFor="ioudf892">{placeholder}</label>
            </div>
          </div>
          {this.renderGroup()}
        </Fragment>
      );
    } else if (departmentsGroups) {
      const options = [{label: 'TOUS LES DEPARTEMENTS', value: ''}];
      departmentsGroups.forEach(departmentsGroup => options.push(
        {label: departmentsGroup.title, value: departmentsGroup.title}
      ));

      return (
        <Fragment>
          <div className="page__head mb-3">
            <h3>Départements</h3>
            <DropdownRadioInput inputChanged={this.handleDropdownChange} options={options}/>
          </div>
          <div className={rowClassName.build()}>
            {this.renderDepartements()}
          </div>
        </Fragment>
      );
    } else if (arbitresGroups) {
      const options = [{label: 'Tous les arbitres', value: ''}];
      arbitresGroups.forEach(arbitresGroup => options.push(
        {label: arbitresGroup.title, value: arbitresGroup.title}
      ));

      return (
        <Fragment>
          <div className="page__head mb-3 mt-4">
            <h3>Tous les arbitres de la saison</h3>
            <DropdownRadioInput 
              inputChanged={this.handleDropdownChange} 
              options={options} />
          </div>
          <div className={rowClassName.build()}>
            {this.renderArbitres()}
          </div>
        </Fragment>
      );
    }
    return '';
  }
}

export default MemberPageDetail;
